import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import type { SortOrder } from '@/api/list'
import { BREAKDOWN_MAX_LIMIT, type BreakdownDimension, type BreakdownRow, type RevenueBasis, type RevenueBreakdown, type RevenueNullReason } from '@/api/revenue'
import { ModelSelect } from '@/components/ModelSelect'
import { Card, EmptyState, formatChange, formatInteger, formatValue, SegmentedControl, type FormatOptions } from '@/design'
import { ListTable } from '@/features/analytics/list/ListTable'
import { Unavailable, UNDEFINED } from '@/features/analytics/list/cells'
import type { ListColumn, NotAvailable } from '@/features/analytics/list/nullState'
import { CardQueryBody } from '@/features/overview/CardQueryBody'
import { useModel } from '@/features/marketing/attribution/attributionQuery'
import type { GlobalFilters } from '@/lib/filters'
import { useFilterSearch } from '@/lib/useGlobalFilters'
import { useUrlChoice } from '@/lib/useUrlChoice'

import { BASIS_NOUN, BREAKDOWN_LABELS, comparisonLabel, NULL_REASON_TEXT, sentence, share as formatShare } from '../format'
import { BY_PARAM } from '../params'
import { useRevenueBreakdown } from '../queries'

/** Which dimensions each breakdown tab offers, the first its default. */
export const TAB_DIMENSIONS = {
  competition: ['competition', 'prize_category'],
  channel: ['channel', 'campaign'],
  customer: ['customer_type'],
} as const satisfies Record<string, readonly BreakdownDimension[]>
export type BreakdownTabKey = keyof typeof TAB_DIMENSIONS

interface Context {
  notAvailable: NotAvailable<BreakdownRow, RevenueNullReason>
  money: FormatOptions
  /** The filters' query string, carried by a competition's link. */
  search: string
  by: BreakdownDimension
}

const describe = (reason: RevenueNullReason) => ({ label: 'Not available', why: `${NULL_REASON_TEXT[reason]}.` })

/** Why a row's null is missing for a reason: the share for the whole table, the AOV per row. */
function rule(row: BreakdownRow, field: keyof BreakdownRow, notAvailable: NotAvailable<BreakdownRow, RevenueNullReason>): RevenueNullReason | null {
  if (field === 'aov') return row.aovNullReason
  if (field === 'share') return notAvailable.share ?? null
  return null
}

function missing(row: BreakdownRow, field: keyof BreakdownRow, context: Context): ReactNode {
  const reason = rule(row, field, context.notAvailable)
  return reason ? <Unavailable {...describe(reason)} /> : UNDEFINED
}

function change(row: BreakdownRow, money: FormatOptions): ReactNode {
  if (!row.delta) return UNDEFINED
  if (row.delta.ratio !== null) return formatChange(row.delta.ratio)
  // Up from nothing: no ratio to give, so the change in money.
  const sign = row.delta.absolute > 0 ? '+' : row.delta.absolute < 0 ? '−' : ''
  return `${sign}${formatValue(Math.abs(row.delta.absolute), 'currency', money)}`
}

const COLUMNS: readonly ListColumn<BreakdownRow, Context>[] = [
  {
    key: 'label',
    label: 'Name',
    group: 'Row',
    byDefault: true,
    cell: (row, { search, by }) =>
      row.kind === 'value' && by === 'competition' ? (
        <Link to={`/analytics/competitions/${encodeURIComponent(row.key)}${search}`}>{row.label}</Link>
      ) : (
        <span className={row.kind === 'value' ? undefined : 'list-muted'}>{row.label}</span>
      ),
  },
  { key: 'revenue', label: 'Revenue', group: 'Revenue', sort: 'revenue', byDefault: true, align: 'end', cell: (row, { money }) => formatValue(row.revenue, 'currency', money) },
  {
    key: 'share',
    label: 'Share',
    group: 'Revenue',
    field: 'share',
    sort: 'share',
    byDefault: true,
    align: 'end',
    cell: (row, context) => (row.share === null ? missing(row, 'share', context) : formatShare(row.share)),
  },
  { key: 'orders', label: 'Orders', group: 'Orders', sort: 'orders', byDefault: true, align: 'end', cell: (row, { money }) => formatInteger(row.orders, money) },
  {
    key: 'aov',
    label: 'AOV',
    group: 'Orders',
    field: 'aov',
    sort: 'aov',
    byDefault: true,
    align: 'end',
    cell: (row, context) => (row.aov === null ? missing(row, 'aov', context) : formatValue(row.aov, 'currency', context.money)),
  },
  { key: 'tickets', label: 'Tickets', group: 'Orders', sort: 'tickets', byDefault: true, align: 'end', cell: (row, { money }) => formatInteger(row.tickets, money) },
  {
    key: 'previous',
    label: 'Comparison',
    group: 'Comparison',
    byDefault: false,
    align: 'end',
    cell: (row, { money }) => (row.previousRevenue === null ? UNDEFINED : formatValue(row.previousRevenue, 'currency', money)),
  },
  { key: 'delta', label: 'Change', group: 'Comparison', sort: 'delta', byDefault: true, align: 'end', cell: (row, { money }) => change(row, money) },
]

const GROUP_LABELS: Record<string, string> = { Row: 'Row', Revenue: 'Revenue', Orders: 'Orders', Comparison: 'Comparison' }

const SORT_VALUES: Record<string, (row: BreakdownRow) => number | null> = {
  revenue: (row) => row.revenue,
  share: (row) => row.share,
  orders: (row) => row.orders,
  aov: (row) => row.aov,
  tickets: (row) => row.tickets,
  delta: (row) => row.delta?.ratio ?? null,
}

/**
 * The rows in the chosen order: values sorted by the column (nulls last, then the API's
 * order), the `other` row and the fixed rows pinned after them in the API's order — they
 * are not ranked against the values they are not.
 */
export function sortRows(rows: readonly BreakdownRow[], sort: { sort: string; order: SortOrder } | null): BreakdownRow[] {
  const values = rows.filter((row) => row.kind === 'value')
  const pinned = rows.filter((row) => row.kind !== 'value')
  const read = sort ? SORT_VALUES[sort.sort] : undefined
  if (!sort || !read) return [...values, ...pinned]
  const ranked = values
    .map((row, index) => ({ row, index, value: read(row) }))
    .sort((a, b) => {
      if (a.value === null || b.value === null) return a.value === b.value ? a.index - b.index : a.value === null ? 1 : -1
      const order = sort.order === 'asc' ? a.value - b.value : b.value - a.value
      return order !== 0 ? order : a.index - b.index
    })
    .map(({ row }) => row)
  return [...ranked, ...pinned]
}

const DEFAULT_SORT = { sort: 'revenue', order: 'desc' as SortOrder }

function BreakdownTable({ data, money }: { data: RevenueBreakdown; money: FormatOptions }) {
  const [sort, setSort] = useState<{ sort: string; order: SortOrder } | null>(null)
  const search = useFilterSearch()
  const notAvailable: NotAvailable<BreakdownRow, RevenueNullReason> = {}
  if (data.shareNullReason) notAvailable.share = data.shareNullReason
  const aovReason = data.rows.find((row) => row.aovNullReason)?.aovNullReason
  if (aovReason) notAvailable.aov = aovReason
  const rows = sortRows(data.rows, sort)

  return (
    <ListTable<BreakdownRow, Context, RevenueNullReason>
      caption={`Revenue by ${BREAKDOWN_LABELS[data.by].toLowerCase()}`}
      columns={COLUMNS}
      groupLabels={GROUP_LABELS}
      columnChoice={{ storageKey: 'ci.revenue.breakdown.columns', keys: COLUMNS.map((column) => column.key), defaults: COLUMNS.filter((column) => column.byDefault).map((column) => column.key) }}
      sort={sort ?? DEFAULT_SORT}
      onSort={setSort}
      rows={rows}
      rowKey={(row) => `${row.kind}:${row.key}`}
      context={{ notAvailable, money, search, by: data.by }}
      rule={rule}
      describe={describe}
      lead={() => (
        <>
          Total {formatValue(data.total.revenue, 'currency', money)} · {formatInteger(data.total.orders, money)} orders · {formatInteger(data.total.tickets, money)} tickets
          {' · '}
          {comparisonLabel(data)}. Each order counts once on every row it has revenue on.
        </>
      )}
      empty={<EmptyState variant="no-results" compact />}
      footer={null}
    />
  )
}

/**
 * A breakdown tab: the period's revenue under the basis by competition or prize category,
 * channel or campaign (under the chosen attribution model), or customer type — share,
 * orders, AOV, tickets and the change, sortable, the `other` and fixed rows always last.
 * The route offers no CSV, so there is no export here.
 */
export function BreakdownTab({ tab, filters, basis, money }: { tab: BreakdownTabKey; filters: GlobalFilters; basis: RevenueBasis; money: FormatOptions }) {
  const options = TAB_DIMENSIONS[tab]
  const [by, setBy] = useUrlChoice<BreakdownDimension>(BY_PARAM, options, options[0])
  const [model, setModel] = useModel()
  const attributed = by === 'channel' || by === 'campaign'
  const read = useRevenueBreakdown(filters, basis, by, { limit: BREAKDOWN_MAX_LIMIT, model: attributed ? model : undefined })

  return (
    <Card
      title={`Revenue by ${BREAKDOWN_LABELS[by].toLowerCase()}`}
      subtitle={`${sentence(BASIS_NOUN[basis])}${attributed ? ' · credited by the chosen model' : ''}`}
      busy={read.isFetching}
      actions={
        <div className="rv-actions">
          {options.length > 1 ? (
            <SegmentedControl label="Break down by" options={options.map((value) => ({ value, label: BREAKDOWN_LABELS[value] }))} value={by} onChange={setBy} />
          ) : null}
          {attributed ? <ModelSelect value={model} onChange={setModel} /> : null}
        </div>
      }
    >
      <CardQueryBody query={read} loadingRows={8} errorDescription="The breakdown could not be loaded.">
        {({ data }) => <BreakdownTable data={data} money={money} />}
      </CardQueryBody>
    </Card>
  )
}
